import { useClientPortalAPI } from "@certn/api";
import {
  CheckboxFormikField,
  Icon,
  SelectFormikField,
  SelectItem,
  SummaryMessage,
  TextInputFormikField,
  Text,
  SectionMessage,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useFormikContext } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAllowedCurrencyForSubsidiary } from "../../hooks/useAllowedCurrencyForSubsidiary";
import { useClientAccount } from "../../hooks/useClientAccount";
import { useSubsidiaryCheckEligibility } from "../../hooks/useSubsidiaryCheckEligibility";
import {
  payerOptions,
  paymentMethods,
  subsidiaries,
  Subsidiary,
  type PayerOption,
} from "../../types";
import { AddPaymentProfileModal } from "../payment-profiles/components/AddPaymentProfileModal";
import {
  doesCardHaveIcon,
  getPaymentIcon,
  isCardExpired,
} from "../payment-profiles/helpers";
import { BillingProfile } from "./CreateBillingProfileContext";
import { useFlags } from "launchdarkly-react-client-sdk";

interface Props {
  onPayerOptionChange?: (isApplicant: boolean) => void;
  allowedPaymentMethods?: string[];
}

export function BillingProfileForm(props: Props) {
  const { caseManagementEnableApplicantPayer } = useFlags();
  const { values: formValues, setFieldValue } =
    useFormikContext<BillingProfile>();
  const { client } = useClientAccount();
  const { isEligibleForSubsidiaryCheck, isMismatched } =
    useSubsidiaryCheckEligibility(formValues.address?.country || undefined);

  const { billingProfileId } = useParams();
  const { t } = useTranslation();
  const subsidiary = client?.subsidiary as Subsidiary;
  const currencies = useAllowedCurrencyForSubsidiary(subsidiary);
  const subsidiaryIsNotUSA = subsidiary !== subsidiaries.CERTN_USA;
  const allowedMethods = props.allowedPaymentMethods;

  const { data, mutate: mutatePaymentProfiles } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/${client?.id}/payment-profiles/`
  );

  const [showNewPaymentModal, setShowNewPaymentModal] = useState(false);

  // Historically, some payment profiles could be left incomplete, without a number.
  // So we filter here to make sure they were completed.
  const activePaymentProfiles = useMemo(
    () =>
      data?.results.filter(
        (profile) => profile.number && profile.status === "ACTIVE"
      ) || [],
    [data?.results]
  );

  const showPaymentProfilesField =
    formValues?.payment_method === paymentMethods.CREDIT_CARD;

  const selectedPaymentProfile = useMemo(
    () =>
      activePaymentProfiles.find(
        (profile) => profile.id === formValues.payment_profile_id
      ),
    [activePaymentProfiles, formValues.payment_profile_id]
  );

  const isEditMode = !!billingProfileId;
  const isApplicantPayerSelected = formValues.payer_option === "APPLICANT";
  const isDailyInvoicing = formValues.invoicing_frequency === "DAILY";

  useEffect(() => {
    if (
      isDailyInvoicing &&
      (formValues.collection_method !== "AUTOMATIC" ||
        formValues.payment_method !== "CREDIT_CARD")
    ) {
      // Daily invoicing only supports automatic collection via credit card
      setFieldValue("collection_method", "AUTOMATIC");
      setFieldValue("payment_method", "CREDIT_CARD");
    }
  }, [
    isDailyInvoicing,
    formValues.collection_method,
    formValues.payment_method,
    setFieldValue,
  ]);

  useEffect(() => {
    if (!formValues.purchase_order_number) {
      setFieldValue("single_use_po", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.purchase_order_number]);
  const availablePayerOptions = client?.allowed_payer_options?.filter(
    (option) => {
      if (!caseManagementEnableApplicantPayer && option === "APPLICANT") {
        return false;
      }
      return true;
    }
  );

  const getPayerLabel = (option: PayerOption) => {
    // Using explicit switch to allow i18next-parser to extract keys
    switch (option) {
      case "CLIENT":
        return t("Client");
      case "APPLICANT":
        return t("Applicant");
      default:
        return option;
    }
  };

  const handlePayerOptionChange = (value: PayerOption) => {
    setFieldValue("payer_option", value);

    const isApplicant = value === payerOptions.APPLICANT;

    const setTypedBillingField = <K extends keyof BillingProfile>(
      field: K,
      value: BillingProfile[K]
    ) => {
      setFieldValue(field, value);
    };

    // Clear fields when switching to applicant
    if (isApplicant) {
      setTypedBillingField("sales_tax_number", null);
      setTypedBillingField("purchase_order_number", null);
      setTypedBillingField("single_use_po", false);
      setTypedBillingField("invoicing_frequency", "DAILY");
      setTypedBillingField("collection_method", "AUTOMATIC");
      setTypedBillingField("payment_method", "CREDIT_CARD");
      setTypedBillingField("payment_profile_id", null);
    }

    // Notify parent of the change
    props.onPayerOptionChange?.(isApplicant);
  };

  return (
    <>
      <TextInputFormikField
        name="profile_name"
        testID="billing-information-profile-name"
        label={t("Profile name")}
        isRequired
      />
      <TextInputFormikField
        name="customer_name"
        testID="billing-information-customer-name"
        label={t("Company name")}
        isRequired
      />
      {isApplicantPayerSelected && (
        <SectionMessage type="warning" icon="warning">
          {t(
            "By selecting applicant pay, you are responsible for ensuring compliance with all applicable laws and regulations governing applicant-paid background screening services. Please note that many jurisdictions restrict or prohibit the ability for companies relying on background screening results to require individuals to pay for such services."
          )}
        </SectionMessage>
      )}
      <SelectFormikField
        name="payer_option"
        isRequired
        label={t("Payer")}
        testID="billing-information-payer"
        isDisabled={isEditMode}
        onSelectionChange={(value) =>
          handlePayerOptionChange(value as PayerOption)
        }
        description={
          isEditMode
            ? t("Field can only be changed by creating a new Billing Profile.")
            : undefined
        }
      >
        {availablePayerOptions?.map((option) => (
          <SelectItem key={option} id={option}>
            {getPayerLabel(option)}
          </SelectItem>
        ))}
      </SelectFormikField>
      {!isApplicantPayerSelected && (
        <TextInputFormikField
          name="sales_tax_number"
          testID="billing-information-sales-tax-number"
          label={t("Sales tax number")}
          isNullable
        />
      )}
      {!isApplicantPayerSelected && (
        <TextInputFormikField
          name="purchase_order_number"
          testID="billing-information-purchase-order-number"
          label={t("Purchase order number")}
          isNullable
        />
      )}
      {!isApplicantPayerSelected && formValues.purchase_order_number && (
        <CheckboxFormikField
          name="single_use_po"
          testID="billing-information-single-use-po"
        >
          {t("Apply to next invoice only and then clear")}
        </CheckboxFormikField>
      )}
      <SelectFormikField
        name="currency"
        isRequired
        label={t("Currency")}
        testID="billing-information-currency"
        isDisabled={isEditMode}
        description={
          isEditMode
            ? t("Field can only be changed by creating a new Billing Profile.")
            : undefined
        }
      >
        {currencies.map((currency) => (
          <SelectItem key={currency.id} id={currency.id}>
            {currency.name}
          </SelectItem>
        ))}
      </SelectFormikField>
      {!isApplicantPayerSelected && (
        <SelectFormikField
          name="invoicing_frequency"
          label={t("Invoicing frequency")}
          testID="billing-information-invoicing-frequency"
          isRequired
          isDisabled={isEditMode}
          description={
            isEditMode
              ? t(
                  "Field can only be changed by creating a new Billing Profile."
                )
              : undefined
          }
          onSelectionChange={(value) => {
            if (value === "DAILY") {
              setFieldValue("collection_method", "AUTOMATIC");
              setFieldValue("payment_method", "CREDIT_CARD");
            }
          }}
        >
          <SelectItem id="MONTHLY">{t("Monthly")}</SelectItem>
          <SelectItem id="DAILY">{t("Daily")}</SelectItem>
        </SelectFormikField>
      )}
      {!isApplicantPayerSelected && (
        <SelectFormikField
          name="collection_method"
          label={t("Collection method")}
          testID="billing-information-collection-method"
          isRequired
          isDisabled={isDailyInvoicing}
          description={
            isDailyInvoicing
              ? t("Daily invoicing requires automatic collection.")
              : undefined
          }
        >
          <SelectItem id="AUTOMATIC">{t("Automatic")}</SelectItem>
          {!isDailyInvoicing && (
            <SelectItem id="MANUAL">{t("Manual")}</SelectItem>
          )}
        </SelectFormikField>
      )}
      {!isApplicantPayerSelected && (
        <SelectFormikField
          name="payment_method"
          label={t("Payment method")}
          testID="billing-information-payment-method"
          isRequired
        >
          {formValues.collection_method !== "AUTOMATIC" && (
            <>
              {(!allowedMethods ||
                allowedMethods.includes(paymentMethods.BANK_TRANSFER)) && (
                <SelectItem id={paymentMethods.BANK_TRANSFER}>
                  {t("Bank transfer")}
                </SelectItem>
              )}
              {subsidiaryIsNotUSA &&
                (!allowedMethods ||
                  allowedMethods.includes(paymentMethods.CHECK)) && (
                  <SelectItem id={paymentMethods.CHECK}>
                    {t("Cheque")}
                  </SelectItem>
                )}
            </>
          )}
          <SelectItem id={paymentMethods.CREDIT_CARD}>
            {t("Credit card")}
          </SelectItem>
        </SelectFormikField>
      )}
      {!isApplicantPayerSelected &&
        formValues.payment_method === paymentMethods.CREDIT_CARD &&
        !activePaymentProfiles.length && (
          <SummaryMessage type="warning">
            <Text marginTop="space-05" style="global/heading-small">
              {t("No active payment profiles found.")}
            </Text>
            <br />
            <br />
            <Text>
              {t(
                "You must select an active payment profile for credit card payments but we couldn't find any active payment profiles in your account. Please create one first."
              )}
            </Text>
          </SummaryMessage>
        )}
      {!isApplicantPayerSelected && showPaymentProfilesField && (
        <>
          <SelectFormikField
            name="payment_profile_id"
            label={t("Payment profile")}
            isRequired={!(isEligibleForSubsidiaryCheck && isMismatched)}
            testID="billing-information-payment-profile"
            onSelectionChange={(key) => {
              if (key === "add-new-payment-profile") {
                if (typeof data?.count !== "number") {
                  setFieldValue("payment_profile_id", null);
                  return;
                }
                setShowNewPaymentModal(true);
                setFieldValue("payment_profile_id", null);
              }
            }}
          >
            {activePaymentProfiles.map((paymentProfile) => (
              <SelectItem id={paymentProfile.id} key={paymentProfile.id}>
                {paymentProfile.title ||
                  (paymentProfile.number
                    ? `\u2022\u2022\u2022\u2022 ${paymentProfile.number}`
                    : paymentProfile.id)}
              </SelectItem>
            ))}
            <SelectItem
              id="add-new-payment-profile"
              key="add-new-payment-profile"
            >
              {t("Add new payment profile")}
            </SelectItem>
          </SelectFormikField>
          {selectedPaymentProfile && (
            <CreditCardDetails
              brand={selectedPaymentProfile.brand}
              number={selectedPaymentProfile.number}
              expiryDate={selectedPaymentProfile.expiry_date}
              owner={selectedPaymentProfile.owner}
            />
          )}
        </>
      )}
      {showNewPaymentModal && typeof data?.count === "number" && client?.id && (
        <AddPaymentProfileModal
          customerAccountId={client.id}
          handleClose={() => {
            setShowNewPaymentModal(false);
            void mutatePaymentProfiles();
          }}
          includeTitle
          initialPaymentProfileCount={data.count}
        />
      )}
    </>
  );
}

function CreditCardDetails({
  brand,
  number,
  expiryDate,
  owner,
}: {
  brand: string | null;
  number: string | null;
  expiryDate: string | null;
  owner: string | null;
}) {
  const { t } = useTranslation();

  if (!number) return null;

  let formattedExpiry: string | null = null;
  if (expiryDate) {
    const [year, month] = expiryDate.split("-");
    if (year && month) {
      formattedExpiry = `${month}/${year}`;
    }
  }
  const expired = formattedExpiry ? isCardExpired(formattedExpiry) : false;

  return (
    <div className={styles.cardDetails} data-testid="credit-card-details">
      <div className={styles.cardIcon}>
        {brand && doesCardHaveIcon(brand) ? (
          <img
            src={getPaymentIcon(brand)}
            alt={brand}
            width="24px"
            height="24px"
          />
        ) : (
          <Icon name="credit_card" size="space-6" />
        )}
      </div>
      <div className={styles.cardInfo}>
        <Text style="global/body-primary">
          {brand ? `${brand.charAt(0).toUpperCase()}${brand.slice(1)}` : ""}{" "}
          {t("ending in")} {"\u2022\u2022\u2022\u2022"} {number}
        </Text>
        <div className={styles.cardBottomRow}>
          {formattedExpiry && (
            <Text
              style="global/body-secondary"
              color={expired ? "background-accent-red-strong" : undefined}
            >
              {expired ? t("Expired") : t("Expires")} {formattedExpiry}
            </Text>
          )}
          {owner && formattedExpiry && (
            <span className={styles.cardDivider}>|</span>
          )}
          {owner && <Text style="global/body-secondary">{owner}</Text>}
        </div>
      </div>
    </div>
  );
}

const styles = {
  cardDetails: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "var(--space-3)",
    padding: "var(--space-3) var(--space-4)",
    borderRadius: "var(--border-radius-2)",
    border: "1px solid var(--border-base)",
    backgroundColor: "var(--surface-raised)",
  }),
  cardIcon: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  cardInfo: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  cardBottomRow: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "var(--space-2)",
  }),
  cardDivider: css({
    color: "var(--text-subtlest)",
    fontSize: "var(--font-size-1)",
  }),
};
